import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { inject } from 'mobx-react'
import { IStores } from 'src/Stores'
import { Formik, Field } from 'formik'
import * as Yup from 'yup'
import { DropdownOption } from 'src/viewModels/DropdownOption'
import _ from 'lodash'
import { IEpisodeOfCare, ILocation, ISchedule } from 'src/Definitions'
import SimpleEventDialog from 'src/ReceiveClaim/SimpleEventDialog'
import ReceiveClaimProviderLookupDialog from 'src/ReceiveClaim/ReceiveClaimProviderLookupDialog'
import { Refresh } from 'mdi-material-ui'
import { processAction, validateCreateClaim } from 'src/services/WorkflowService'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { getEpisodesOfCareByPatientId, searchPatients } from 'src/services/PatientService'
import { SearchRequestBase } from 'src/viewModels/SearchRequestBase'
import { EpisodeOfCareSummaryResult } from 'src/viewModels/EpisodeOfCareSummaryResult'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import moment from 'moment'
import { ClaimEventSummary } from 'src/viewModels/workflow/ClaimEventSummary'
import { PatientSearchRequest } from 'src/viewModels/PatientSearchRequest'
import { PatientSummaryResult } from 'src/viewModels/PatientSummaryResult'
import ReactPaginate from 'react-paginate'
import DatePickerField from 'src/common/DatePickerField/DatePickerField'
import { CreateClaimValidationResult } from 'src/viewModels/workflow/CreateClaimValidationResult'

interface IAssociatePatientEOCModalProps {
  isOpen: boolean
  closeModal: () => void
  isLoading?: boolean
  setSelectedLocation?: (location?: ILocation) => void
  setSelectedSchedule?: (schedule?: ISchedule) => void
  setSelectedScheduleById?: (id: string) => Promise<void>
  selectedSchedule?: ISchedule
  openDialogWithSchedule?: (schedule?: ISchedule) => void
  openProviderLookupDialog?: () => void
  isProviderLookupDialogOpen?: boolean
  scheduleItems?: DropdownOption[]
  locationItems?: DropdownOption[]
  episodeOfCare?: IEpisodeOfCare
  getEpisodeOfCareById?: (id: string) => Promise<void>
  action: InboxActionSummary
  inboxItemId: number
  updateClaim?: boolean
  rerouteToPath?: (path: string) => void
  getSchedules?: () => Promise<void>
  closeTab?: () => void
}

const AssociatePatientEOCModal: React.FC<IAssociatePatientEOCModalProps> = observer(
  (props: IAssociatePatientEOCModalProps) => {
    const formikRef = React.useRef<any>(null)
    const [selectedEOCId, setSelectedEOCId] = useState<string>()
    const searchValidationSchema = Yup.object().shape({
      patientDob: Yup.mixed().test('valid-date', 'Invalid date format', function (value) {
        if (!value) return true
        let dateString = value
        if (value instanceof Date) {
          dateString = moment(value).format('MM/DD/YYYY')
        }
        const isValid = moment(dateString, 'MM/DD/YYYY', true).isValid()
        return isValid
      }),
    })
    const [modalPage, setModalPage] = useState(1)
    const [showHistoricalModal, setShowHistoricalModal] = useState(false)
    const [disableProvider, setDisableProvider] = useState(true)
    const [disableDropdown, setDisableDropdown] = useState(true)
    const [hasSelectedSchedule, setHasSelectedSchedule] = useState(false)
    const [selectedLocationId, setselectedLocationId] = useState<string>()
    const [isLoading, setIsLoading] = useState(false)
    const [gridData, setGridData] = useState<EpisodeOfCareSummaryResult[]>()
    const [hasPatient, setHasPatient] = useState(false)
    const [comments, setComments] = useState('')
    const [executed, setExecuted] = useState(false)
    const [patientGridData, setPatientGridData] = useState<PatientSummaryResult[]>()
    const [searchPatientLastName, setSearchPatientLastName] = useState('')
    const [searchPatientFirstName, setSearchPatientFirstName] = useState('')
    const [searchDob, setSearchDob] = useState('')
    const [patientPage, setPatientPage] = useState(1)
    const [patientPageCount, setPatientPageCount] = useState(0)
    const [patientTotalItems, setPatientTotalItems] = useState(0)
    const [patientFirstRecordIndex, setPatientFirstRecordIndex] = useState(0)
    const [patientLastRecordIndex, setPatientLastRecordIndex] = useState(0)
    const [callSearchPatients, setCallSearchPatients] = useState(false)
    const [validation, setValidation] = useState<CreateClaimValidationResult | null>(null)
    const [isValidating, setIsValidating] = useState(false)

    useEffect(() => {
      if (patientTotalItems > 0) {
        setPaginationOffsetData()
      }
      setIsLoading(false)
    }, [patientGridData])

    useEffect(() => {
      if (!callSearchPatients) {
        return
      }
      getPatientData()
    }, [patientPage, callSearchPatients])

    useEffect(() => {
      if (
        selectedLocationId &&
        props.selectedSchedule &&
        selectedEOCId &&
        !executed &&
        !isLoading &&
        modalPage === 3
      ) {
        validateClaim()
      } else {
        setValidation(null)
      }
    }, [
      selectedLocationId,
      props.selectedSchedule,
      selectedEOCId,
      executed,
      isLoading,
      modalPage,
    ])

    const patientPageSize = 5

    const validateClaim = async () => {
      setIsValidating(true)

      try {
        const result = await validateCreateClaim({
          locationId: selectedLocationId!,
          scheduleId: props.selectedSchedule!.id,
          episodeOfCareId: selectedEOCId!,
        })

        setValidation(result)

        if (result.hasDuplicates) {
          console.warn('Duplicate claims detected:', result.duplicateClaimSummary)
        }
      } catch (error) {
        console.error('Validation failed:', error)
        setValidation(null)
      } finally {
        setIsValidating(false)
      }
    }

    const handleExecuteClick = () => {
      setExecuted(true)
      setIsLoading(true)
      save()
    }

    async function save(
      modalLocationId?: string,
      newComments?: string,
      scheduleIdParam?: string
    ) {
      var action = props.action

      var data = new ObjectiveProcessAction()
      var summary = new ClaimEventSummary()

      summary.episodeOfCareId = selectedEOCId ?? ''
      summary.locationId = modalLocationId ?? selectedLocationId ?? ''
      summary.scheduleId = scheduleIdParam ?? props.selectedSchedule?.id ?? ''

      data.claimEventData = summary
      data.comment = newComments ?? comments

      try {
        const response = await processAction(
          data,
          action.id.toString(),
          props.inboxItemId.toString()
        )

        if (action?.closeAfterProcessing && response.nextAssociatedItem === undefined) {
          props.closeTab!()
        } else if (props.updateClaim) {
          window.location.reload()
        } else if (
          !action?.closeAfterProcessing &&
          response.nextAssociatedItem.inboxId == undefined
        ) {
          props.rerouteToPath!(
            `/workflow/claim/${response.nextAssociatedItem.itemId}/default-inbox`
          )
        } else {
          props.rerouteToPath!(
            `/workflow/claim/${response.nextAssociatedItem.itemId}/inbox/${response.nextAssociatedItem.inboxId}`
          )
        }
      } catch (error: any) {
        console.error('Failed to create claim:', error)
        setExecuted(false)
        setIsLoading(false)
        close()
      }
    }

    function close() {
      props.closeModal()
      setModalPage(1)
      setGridData(undefined)
      setHasPatient(false)
      setExecuted(false)
      setValidation(null)
      clearSearch()
    }

    function search(event: any) {
      const formValues = formikRef.current?.values
      if (formValues) {
        setSearchPatientFirstName(formValues.patientFirst || '')
        setSearchPatientLastName(formValues.patientLast || '')
        setSearchDob(formValues.patientDob || '')
      }
      setCallSearchPatients(true)
      setPatientPage(1)
      event.preventDefault()
    }

    function clearSearch() {
      formikRef.current?.resetForm({
        values: {
          patientFirst: '',
          patientLast: '',
          patientDob: '',
        },
      })

      setPatientGridData(undefined)
      setSearchPatientFirstName('')
      setSearchPatientLastName('')
      setSearchDob('')
      setCallSearchPatients(false)
      setPatientPage(1)
      setPatientPageCount(0)
      setPatientTotalItems(0)
    }

    async function getPatientData() {
      setIsLoading(true)
      const requestData = new PatientSearchRequest()
      requestData.page = patientPage
      requestData.pageSize = patientPageSize
      requestData.patientFirstName = searchPatientFirstName
      requestData.patientLastName = searchPatientLastName
      requestData.dateOfBirth = searchDob
        ? moment(searchDob).format('YYYY-MM-DD')
        : undefined
      await searchPatients(requestData).then((response: any) => {
        setPatientTotalItems(response.totalItems)
        setPatientPageCount(response.totalPages)
        setPatientGridData(response.items)
        setCallSearchPatients(false)
      })
    }

    function isGridDataEmpty() {
      return patientTotalItems === 0
    }

    function paginationInfo() {
      if (!isGridDataEmpty()) {
        return (
          <div className="pagination">
            <Typography variant="caption" gutterBottom>
              Showing {patientFirstRecordIndex} to {patientLastRecordIndex} of{' '}
              {patientTotalItems} entries
            </Typography>
          </div>
        )
      } else {
        return <></>
      }
    }

    function mapPatientGridData() {
      if (isGridDataEmpty()) {
        return (
          <TableRow key={1}>
            <TableCell colSpan={13} align="center">
              No records found
            </TableCell>
          </TableRow>
        )
      } else {
        return patientGridData?.map((patient) => (
          <TableRow key={patient.patientId} className="gridPadding">
            <TableCell>
              <Button
                color="primary"
                variant="contained"
                onClick={() => setSelectedPatientRow(patient.patientId!)}
              >
                Select
              </Button>
            </TableCell>
            <TableCell style={{ paddingLeft: '30px' }}>
              {patient.patientIdNumber}
            </TableCell>
            <TableCell>{patient.preCertNumber}</TableCell>
            <TableCell>{patient.lastName}</TableCell>
            <TableCell>{patient.firstName}</TableCell>
            <TableCell align="right">
              {patient.dateOfBirth
                ? moment.utc(patient.dateOfBirth).format('MM/DD/YYYY')
                : ''}
            </TableCell>
          </TableRow>
        ))
      }
    }

    function setPaginationOffsetData() {
      const firstRecordIndex = (patientPage - 1) * patientPageSize + 1
      const lastRecordIndex =
        patientPage * patientPageSize < patientTotalItems
          ? patientPage * patientPageSize
          : patientTotalItems
      setPatientFirstRecordIndex(firstRecordIndex)
      setPatientLastRecordIndex(lastRecordIndex)
    }

    function handlePageChange(event: any) {
      const page = event.selected
      setCallSearchPatients(true)
      setPatientPage(page + 1)
    }

    const setSelectedPatientRow = (patientId: string) => {
      setHasPatient(true)
      setModalPage(2)
      setCallSearchPatients(false)
      getEOCData(patientId)
    }

    const setSelectedEOC = async (id?: string) => {
      if (id != undefined) {
        setIsLoading(true)
        setSelectedEOCId(id)
        setModalPage(3)
        await props.getEpisodeOfCareById!(id)
        await props.getSchedules!()
        setIsLoading(false)
      }
    }

    const changeSelectHandler = async (event: SelectChangeEvent<string>) => {
      const target = event.target as HTMLSelectElement
      target.options[0].disabled = true
      setIsLoading(true)
      setDisableProvider(false)
      setDisableDropdown(false)
      setHasSelectedSchedule(true)
      setselectedLocationId(undefined)
      await props.setSelectedScheduleById!(target.selectedOptions[0].accessKey)
      var d = document.getElementById('locationSelect') as HTMLSelectElement
      d.options[0].disabled = false
      d.selectedIndex = 0
      setIsLoading(false)
    }

    const changeLocationHandler = (event: SelectChangeEvent<string>) => {
      const target = event.target as HTMLSelectElement
      target.options[0].disabled = true
      setselectedLocationId(target.selectedOptions[0].accessKey)
    }

    const mapGridData = () => {
      if (gridData == undefined || gridData?.length <= 0) {
        return (
          <TableRow key={1}>
            <TableCell colSpan={10} align="center">
              No records found
            </TableCell>
          </TableRow>
        )
      } else {
        return gridData?.map((eoc, index) => (
          <TableRow key={index} className="gridPadding">
            <TableCell>
              <Button
                onClick={() => setSelectedEOC(eoc.episodeOfCareId)}
                color="primary"
                variant="contained"
              >
                Select
              </Button>
            </TableCell>
            <TableCell> {eoc.referralDateDisplay} </TableCell>
            <TableCell>{eoc.takeoverOfCare}</TableCell>
            <TableCell>{eoc.generatedNumberOrLegacy}</TableCell>
            <TableCell>{eoc.status}</TableCell>
            <TableCell>{eoc.surgeryDateDisplay} </TableCell>
            <TableCell>{eoc.lastMMIDateDisplay}</TableCell>
            <TableCell>{eoc.careCoordinatorName}</TableCell>
            <TableCell>{eoc.bodyPart}</TableCell>
            <TableCell>
              {eoc.billingNotes && (
                <TextField
                  value={eoc.billingNotes}
                  multiline
                  fullWidth
                  disabled
                  minRows={3}
                  variant="standard"
                  sx={{ minWidth: '500px' }}
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      fontSize: 'inherit',
                      color: 'inherit',
                      WebkitTextFillColor: 'inherit',
                    },
                  }}
                  inputProps={{
                    style: {
                      padding: 0,
                      color: 'inherit',
                      WebkitTextFillColor: 'inherit',
                    },
                  }}
                />
              )}
            </TableCell>
          </TableRow>
        ))
      }
    }

    const getEOCData = async (patientId: string) => {
      setIsLoading(true)
      const requestData = new SearchRequestBase()
      requestData.page = 1
      requestData.pageSize = 100
      await getEpisodesOfCareByPatientId(requestData, patientId).then((response: any) => {
        setIsLoading(false)
        setGridData(response.items)
      })
    }

    var modalTitle = props.action.actionName

    const openHistoricalModal = () => {
      setShowHistoricalModal(true)
      setHasSelectedSchedule(false)
      props.setSelectedLocation!(undefined)
      props.setSelectedSchedule!(undefined)

      var d = document.getElementById('locationSelect') as HTMLSelectElement
      d.options[0].disabled = false
      d.selectedIndex = 0
      var e = document.getElementById('eventSelect') as HTMLSelectElement
      e.options[0].disabled = false
      e.selectedIndex = 0
      props.openDialogWithSchedule!(undefined)
    }

    const openProviderModal = () => {
      props.openProviderLookupDialog!()
      var d = document.getElementById('locationSelect') as HTMLSelectElement
      d.options[0].disabled = false
      d.selectedIndex = 0
    }

    const refreshProviderList = async () => {
      await props.setSelectedScheduleById!(props.selectedSchedule!.id)
    }

    var eventDialog = showHistoricalModal && (
      <SimpleEventDialog
        isOpen={showHistoricalModal}
        episodeOfCare={props.episodeOfCare}
        updateParentStateAfterModal={(locationId?: string, scheduleId?: string) => {
          if (!locationId) return
          setIsLoading(true)
          setHasSelectedSchedule(true)
          setselectedLocationId(locationId)
          save(
            locationId,
            props.action.requireComment ? 'Simple Event' : undefined,
            scheduleId
          )
        }}
        updateParentStateAfterClose={() => {
          setShowHistoricalModal(false)
          props.setSelectedSchedule!(undefined)
          props.setSelectedLocation!(undefined)
          setHasSelectedSchedule(false)
        }}
      />
    )
    var providerLookupDialog = props.isProviderLookupDialogOpen && (
      <ReceiveClaimProviderLookupDialog
        open={props.isProviderLookupDialogOpen!}
        isOpen={props.isProviderLookupDialogOpen!}
        updateParentStateAfterModal={
          showHistoricalModal
            ? undefined
            : (locationId?: string, scheduleId?: string) => {
                if (!locationId) return
                setIsLoading(true)
                setselectedLocationId(locationId)
                save(
                  locationId,
                  props.action.requireComment ? 'Additional Provider' : undefined,
                  scheduleId
                )
              }
        }
      />
    )

    const renderInlineDuplicateWarning = () => {
      if (!validation?.hasDuplicates || !validation.duplicateClaimSummary) return null

      const duplicateData = validation.duplicateClaimSummary

      return (
        <Grid container spacing={2} style={{ marginTop: '20px' }}>
          <Grid item xs={12}>
            <Typography>
              {duplicateData.duplicateClaimCount === 1 ? (
                <>
                  <strong>A claim</strong> already exists that matches the Provider Group,
                  Date of Service, and EOC Number on this claim. Click Execute to continue
                  saving this claim or click Close to return to the current claim.
                </>
              ) : (
                <>
                  <strong>{duplicateData.duplicateClaimCount} claims</strong> already
                  exist that match the Provider Group, Date of Service, and EOC Number on
                  this claim. Click Execute to continue saving this claim or click Close
                  to return to the current claim.
                </>
              )}
            </Typography>
          </Grid>

          <Grid container direction="row" item xs={12}>
            {/* Previous Claim(s) Column */}
            <Grid container direction="column" spacing={1} xs={6}>
              <h1>
                Previous {duplicateData.duplicateClaimCount === 1 ? 'Claim' : 'Claims'}
              </h1>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item>
                    <strong>Patient Name:</strong>
                  </Grid>
                  <Grid item>
                    <span>{duplicateData.patientName}</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item>
                    <strong>EOC/Legacy:</strong>
                  </Grid>
                  <Grid item xs={9}>
                    <span>{duplicateData.duplicateEOCLegacyGroup}</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item>
                    <strong>Body Part:</strong>
                  </Grid>
                  <Grid item>
                    <span>{duplicateData.bodyPart}</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item>
                    <strong>Provider Group:</strong>
                  </Grid>
                  <Grid item>
                    <span>{duplicateData.providerGroup}</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item>
                    <strong>Provider Type:</strong>
                  </Grid>
                  <Grid item>
                    <span>{duplicateData.providerTypeList}</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item>
                    <strong>Date of Service:</strong>
                  </Grid>
                  <Grid item>
                    <span>
                      {moment(duplicateData.dateOfService).format('MM/DD/YYYY')}
                    </span>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Current Claim Column */}
            <Grid container direction="column" spacing={1} xs={6}>
              <h1>Current Claim</h1>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item>
                    <strong>Patient Name:</strong>
                  </Grid>
                  <Grid item>
                    <span>{duplicateData.patientName}</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item>
                    <strong>EOC/Legacy:</strong>
                  </Grid>
                  <Grid item>
                    <span>{duplicateData.newEOCLegacy}</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item>
                    <strong>Body Part:</strong>
                  </Grid>
                  <Grid item>
                    <span>{duplicateData.bodyPart}</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item>
                    <strong>Provider Group:</strong>
                  </Grid>
                  <Grid item>
                    <span>{duplicateData.providerGroup}</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item>
                    <strong>Provider Type:</strong>
                  </Grid>
                  <Grid item>
                    <span>{duplicateData.providerTypeList}</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item>
                    <strong>Date of Service:</strong>
                  </Grid>
                  <Grid item>
                    <span>
                      {moment(duplicateData.dateOfService).format('MM/DD/YYYY')}
                    </span>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )
    }

    var targetScheduleItems = props.scheduleItems ?? []
    var targetLocationItems = props.locationItems ?? []
    return (
      <>
        <Dialog
          fullWidth={true}
          maxWidth="xl"
          open={props.isOpen!}
          onClose={(_, reason) => {
            if (reason !== 'backdropClick') {
              close()
            }
          }}
        >
          <DialogTitle style={{ padding: 0 }}>
            <ResponsiveAppBar title={modalTitle} isModal={true} />
          </DialogTitle>
          <DialogContent style={{ padding: '12px 12px 8px' }}>
            {isLoading && (
              <div
                style={{
                  position: 'fixed',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  cursor: 'pointer',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: '1200',
                }}
              >
                <CircularProgress size={100} />
              </div>
            )}
            {modalPage == 1 && (
              <Formik
                initialValues={{ patientFirst: '', patientLast: '', patientDob: '' }}
                initialTouched={{
                  patientFirst: false,
                  patientLast: false,
                  patientDob: false,
                }}
                onSubmit={search}
                validationSchema={searchValidationSchema}
                innerRef={formikRef}
                validateOnChange={true}
                validateOnBlur={true}
              >
                {(formik) => (
                  <form>
                    <Grid item container direction="column" spacing={2} xs={12}>
                      <Grid item>
                        <Typography
                          color="primary"
                          component="h6"
                          variant="h6"
                          gutterBottom
                          style={{ paddingBottom: '10px' }}
                        >
                          SEARCH CRITERIA
                        </Typography>
                        <Grid item container direction="row" spacing={2}>
                          <Grid item xs={2}>
                            <FormControl fullWidth>
                              <Field
                                as={TextField}
                                label="Patient First Name"
                                name="patientFirst"
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={2}>
                            <FormControl fullWidth>
                              <Field
                                as={TextField}
                                label="Patient Last Name"
                                name="patientLast"
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={2}>
                            <DatePickerField
                              inputId="dob"
                              variant="outlined"
                              name="patientDob"
                              label="Date of Birth"
                            />
                          </Grid>
                          <Grid item direction="row" spacing={2}>
                            <Button
                              type="submit"
                              color="primary"
                              size="medium"
                              variant="contained"
                              style={{ marginRight: '10px' }}
                              onClick={search}
                              disabled={!!formik.errors.patientDob}
                            >
                              Search
                            </Button>
                            <Button
                              color="secondary"
                              onClick={clearSearch}
                              size="medium"
                              variant="contained"
                            >
                              Clear Search
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} style={{ paddingTop: '20px' }}>
                        <Table aria-label="simple table">
                          <TableHead>
                            <TableRow className="gridPadding">
                              <TableCell />
                              <TableCell style={{ paddingLeft: '30px' }}>
                                Patient Id
                              </TableCell>
                              <TableCell>Pre-Cert #</TableCell>
                              <TableCell>Last Name</TableCell>
                              <TableCell>First Name</TableCell>
                              <TableCell align="right">DOB</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody style={{ width: '100%' }}>
                            {mapPatientGridData()}
                          </TableBody>
                        </Table>
                        <div className="pagination-row">
                          {paginationInfo()}
                          <ReactPaginate
                            forcePage={patientPage - 1}
                            previousLabel={'<'}
                            nextLabel={'>'}
                            onPageChange={handlePageChange}
                            pageCount={patientPageCount}
                            containerClassName={'pagination'}
                            activeClassName={'active'}
                            //@ts-ignore
                            renderOnZeroPageCount={null}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </Formik>
            )}
            {modalPage == 2 && (
              <Grid item xs={12}>
                {hasPatient && (
                  <>
                    <Typography variant="body1" style={{ color: 'grey' }}>
                      Please select an Episode of Care
                    </Typography>
                    <Table aria-label="simple table">
                      <TableHead>
                        <TableRow className="gridPadding">
                          <TableCell></TableCell>
                          <TableCell>Referral Date</TableCell>
                          <TableCell>Takeover of Care</TableCell>
                          <TableCell>EOC/Legacy</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Surgery Date</TableCell>
                          <TableCell>MMI</TableCell>
                          <TableCell>Care Coordinator</TableCell>
                          <TableCell>Body Part</TableCell>
                          <TableCell>Billing Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>{mapGridData()}</TableBody>
                    </Table>
                  </>
                )}
              </Grid>
            )}
            {modalPage == 3 && (
              <>
                {eventDialog}
                {providerLookupDialog}
                <Grid xs={12}>
                  <strong>
                    {props.episodeOfCare?.referral?.patient?.lastName},{' '}
                    {props.episodeOfCare?.referral?.patient?.firstName} (
                    {moment(props.episodeOfCare?.referral?.patient?.dateOfBirth).format(
                      'L'
                    )}
                    )
                  </strong>{' '}
                  EOC/Legacy&nbsp;
                  <strong>{props.episodeOfCare?.episodeOfCareNumberOrLegacyId}</strong>
                </Grid>
                <Grid
                  item
                  container
                  xs={12}
                  style={{ paddingTop: '20px' }}
                  alignItems="center"
                  spacing={2}
                >
                  <Grid item xs={12} style={{ paddingRight: '20px' }}>
                    <InputLabel shrink={true} variant={'standard'}>
                      Event Info
                    </InputLabel>
                    <Select
                      native={true}
                      fullWidth
                      variant="standard"
                      onChange={changeSelectHandler}
                      id="eventSelect"
                    >
                      <option value="">Please select an event</option>
                      {targetScheduleItems.map((item: DropdownOption) => (
                        <option
                          accessKey={item.id}
                          value={item.description}
                          disabled={!item.isEnabled}
                        >
                          {item.description}
                        </option>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={openHistoricalModal}
                    >
                      ADD EVENT
                    </Button>
                  </Grid>
                </Grid>
                <Grid
                  container
                  xs={12}
                  style={{ paddingTop: '20px' }}
                  alignItems="center"
                  spacing={2}
                >
                  <Grid item xs={12} style={{ paddingRight: '20px' }}>
                    <InputLabel shrink={true} variant={'standard'}>
                      Provider Group (Provider Location) - Billing Address
                    </InputLabel>
                    <Select
                      variant="standard"
                      native={true}
                      fullWidth
                      disabled={disableDropdown}
                      onChange={changeLocationHandler}
                      id="locationSelect"
                    >
                      <option value="">Please select a provider</option>
                      {targetLocationItems.map((item: DropdownOption) => (
                        <option
                          accessKey={item.id}
                          value={item.description}
                          disabled={!item.isEnabled}
                        >
                          {item.description}
                        </option>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={openProviderModal}
                      disabled={disableProvider}
                      style={{ marginRight: '20px' }}
                    >
                      ADD PROVIDER
                    </Button>
                    <Tooltip title="Refresh Provider List" placement="top">
                      <Button
                        variant="contained"
                        onClick={refreshProviderList}
                        disabled={disableProvider}
                      >
                        <Refresh />
                      </Button>
                    </Tooltip>
                  </Grid>
                  {props.action.displayComment && (
                    <Grid item xs={12}>
                      <TextField
                        label="Comments"
                        name="notes"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required={props.action.requireComment}
                        multiline
                        minRows="4"
                        onChange={(event) => setComments(event.target.value)}
                      />
                    </Grid>
                  )}
                </Grid>
                {renderInlineDuplicateWarning()}
              </>
            )}
          </DialogContent>
          <DialogActions>
            {modalPage == 2 && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setModalPage(1)
                  setGridData(undefined)
                }}
              >
                Back
              </Button>
            )}
            {modalPage == 3 && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setModalPage(2)
                    setValidation(null)
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ marginLeft: '4px' }}
                  disabled={
                    executed ||
                    isValidating ||
                    selectedEOCId == undefined ||
                    selectedLocationId == undefined ||
                    hasSelectedSchedule == false ||
                    (props.action.requireComment && comments === '')
                  }
                  onClick={handleExecuteClick}
                >
                  {isValidating ? 'Validating...' : 'Execute'}
                </Button>
              </>
            )}
            <Button
              onClick={() => {
                close()
              }}
              data-cy="close"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }
)

const InjectedAssociatePatientEOCModal = inject<
  IStores,
  IAssociatePatientEOCModalProps,
  Partial<IAssociatePatientEOCModalProps>,
  any
>((stores: IStores) => ({
  isLoading: stores.quotes.isLoading,
  selectedSchedule: stores.schedules.selectedSchedule,
  setSelectedLocation: stores.locations.setSelectedEditClaimLocation,
  setSelectedSchedule: (schedule?: ISchedule) => {
    if (schedule) {
      stores.schedules.setSelectedSchedule(schedule)
    }
  },
  scheduleItems: stores.patientEdit.scheduleItems,
  locationItems: stores.schedules.scheduleItems,
  setSelectedScheduleById: stores.schedules.setSelectedScheduleById,
  getScheduledOptions: stores.patientEdit.getScheduledOptions,
  openDialogWithSchedule: stores.schedules.openDialogWithSchedule,
  openProviderLookupDialog: stores.schedules.openClaimProviderLookupDialog,
  isProviderLookupDialogOpen: stores.schedules.isClaimProviderLookupDialogOpen,
  episodeOfCare: stores.patientEdit.selectedEpisodeOfCare,
  getEpisodeOfCareById: stores.patientEdit.getEpisodeOfCareById,
  getSchedules: stores.patientEdit.getScheduledOptions,
  rerouteToPath: stores.global.rerouteToPath,
}))(AssociatePatientEOCModal)

export default InjectedAssociatePatientEOCModal
